/**
 * Artifact Kind Metadata Types
 *
 * Defines the interface that all artifact kind schemas must satisfy.
 * This ensures type safety for itemKind, artifactCard, and completionAction values.
 *
 * @see docs/plans/schema-first-artifact-architecture.md
 */

// Item Kind and UI Type Definitions

/**
 * Valid ItemKind values - top-level classification of what the user does.
 * This is the source of truth that schemas must conform to.
 */
export const VALID_ITEM_KINDS = ['approval', 'input', 'insight'] as const;
export type ItemKind = (typeof VALID_ITEM_KINDS)[number];

/**
 * Valid ArtifactCard values - determines UI flow/rendering.
 * This is the source of truth that schemas must conform to.
 */
export const VALID_ARTIFACT_CARDS = [
  'approval',
  'approval_review_single_step',
  'approval_review_wizard',
  'input_single_step',
  'input_wizard',
  'insight',
] as const;
export type ArtifactCard = (typeof VALID_ARTIFACT_CARDS)[number];

// Completion Action Definitions

/**
 * Valid CompletionAction values - what happens when user approves/completes.
 *
 * This is METADATA about the artifact kind, used to:
 * - Drive wizard UI (show appropriate fields, labels, descriptions)
 * - Build the correct payload structure
 * - Display action descriptions to users
 *
 * NOTE: This is NOT stored in the envelope. The actual action is determined
 * at runtime by flatPlan.actionType ('sendObject', 'salesforcePatch') or
 * context.approvalMetadata presence.
 */
const VALID_COMPLETION_ACTIONS = [
  'webhook_callback',   // POST data to a webhook URL (flatPlan.actionType = 'sendObject')
  'salesforce_update',  // Update a Salesforce record (flatPlan.actionType = 'salesforcePatch')
  'clay_writeback',     // Write status back to Clay table (context.approvalMetadata)
  'none',               // No action - display only (insights)
] as const;
type CompletionAction = (typeof VALID_COMPLETION_ACTIONS)[number];

// Wizard Step Configuration

/** Configuration for a wizard step */
interface WizardStep {
  id: string;
  label: string;
  groups: readonly string[];
  optional?: boolean;
}

// Artifact Kind Metadata Interface

/**
 * Metadata interface for artifact kind schemas.
 *
 * All artifact schemas must have a `meta` property that satisfies this interface.
 * This ensures:
 * - itemKind is a valid ItemKind value
 * - artifactCard is a valid ArtifactCard value
 * - Required fields are present
 *
 * @example
 * ```ts
 * export const WebhookPostMeta = {
 *   id: 'generic_webhook',
 *   label: 'Generic Webhook',
 *   itemKind: 'approval',  // Type-checked!
 *   artifactCard: 'approval_review_single_step',  // Type-checked!
 *   ...
 * } as const satisfies ArtifactKindMeta;
 * ```
 */
export interface ArtifactKindMeta {
  /** Unique identifier for this artifact kind */
  id: string;

  /** Human-readable label */
  label: string;

  /** Description of what this artifact kind does */
  description: string;

  /** Schema version for migrations */
  version: number;

  /** Whether users can create new artifacts of this kind */
  creatable: boolean;

  /** Top-level classification (approval, input, insight) */
  itemKind: ItemKind;

  /** Artifact card that determines rendering flow */
  artifactCard: ArtifactCard;

  /**
   * What happens when the user approves/completes this artifact.
   *
   * This is METADATA for the wizard UI - it determines:
   * - Which configuration fields to show (callbackUrl, salesforce object, etc.)
   * - What action description to display to users
   * - How to build the correct payload structure
   *
   * NOTE: This is NOT stored in the runtime envelope. The actual action is
   * determined by `flatPlan.actionType` ('sendObject' | 'salesforcePatch').
   */
  completionAction: CompletionAction;

  /** Wizard step configuration */
  wizardSteps: readonly WizardStep[];
}

// Type Guards

/**
 * Check if a string is a valid ItemKind.
 */
export function isItemKind(value: string): value is ItemKind {
  return VALID_ITEM_KINDS.includes(value as ItemKind);
}

/**
 * Check if a string is a valid ArtifactCard.
 */
export function isArtifactCard(value: string): value is ArtifactCard {
  return VALID_ARTIFACT_CARDS.includes(value as ArtifactCard);
}
